/**
 * [BLURY] | 🥭 ExportPanel
 * Reset to the reference look, or write the composition out at the chosen
 * output width as a PNG or AVIF.
 * @build 13.09.26
 * @updated 13.09.26 PHT
 * @author TONYTONY Sàrl
 */
import { useState } from 'react';
import { downloadBlob, exportComposition, isLossy } from '@/engine/export';
import type { ExportFormat } from '@/engine/types';
import { useStudio } from '@/state/useStudio';
import { Button, ButtonGroup } from '../ui/Button';
import { Section, SectionNote } from '../ui/Section';
import { Segmented } from '../ui/Segmented';
import { Row, RowLabel, Slider } from '../ui/Slider';
import styles from './ExportPanel.module.css';

const FORMATS: ReadonlyArray<{ value: ExportFormat; label: string }> = [
  { value: 'png', label: 'png' },
  { value: 'avif', label: 'avif' },
];

export function ExportPanel() {
  const size = useStudio(s => s.size);
  const format = useStudio(s => s.exportFormat);
  const quality = useStudio(s => s.exportQuality);
  const setExportFormat = useStudio(s => s.setExportFormat);
  const setExportQuality = useStudio(s => s.setExportQuality);
  const reset = useStudio(s => s.reset);

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onExport = async () => {
    setBusy(true);
    setError(null);
    try {
      // Read straight from the store: the export renders its own canvas at full
      // output resolution rather than reading back the capped preview.
      const { composition, size: output, seed, previewScale } = useStudio.getState();
      const { blob, filename } = await exportComposition({
        composition,
        size: output,
        seed,
        scale: previewScale,
        format,
        quality,
      });
      downloadBlob(blob, filename);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'the export failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Section badge={4} title="Output">
      <div className={styles.meta}>
        <span className={styles.metaName}>file</span>
        <span className={styles.metaValue}>
          {size.width} × {size.height} · {format}
        </span>
      </div>

      <Row>
        <RowLabel name="format" />
        <Segmented
          aria-label="export format"
          value={format}
          options={FORMATS}
          onChange={setExportFormat}
        />
      </Row>

      {isLossy(format) && (
        <Slider
          label="quality"
          value={Math.round(quality * 100)}
          min={40}
          max={100}
          format={v => `${v}%`}
          onChange={v => setExportQuality(v / 100)}
        />
      )}

      <div style={{ marginTop: 12 }}>
        <ButtonGroup>
          <Button onClick={reset}>reset to ref</Button>
          <Button variant="solid" onClick={onExport} disabled={busy}>
            {busy ? 'exporting…' : `export ${format}`}
          </Button>
        </ButtonGroup>
      </div>

      {isLossy(format) && (
        <SectionNote>
          No browser encodes AVIF from a canvas, so this runs libavif in a worker — the encoder is
          fetched on first use, and a large width takes a few seconds.
        </SectionNote>
      )}
      {error && <p className={styles.error}>Export failed — {error}.</p>}
    </Section>
  );
}
